import { Fragment, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import FiPlanCard from "../../../../components/ui/cards/finances/FiPlanCard";
import {
  createSubscription,
  updateSubscriptionDetails,
} from "../../../../api/payments/subscriptionsAPI";

function FiSubscriptionPlans({
  sectionRef,
  featureData,
  content,
  allowedPlanCodes = null,
  isPopup = false,
  isUpgrade = false,
  currentSubscriptionUuid = null,
}) {
  const { isAuthenticated } = useSelector((state) => state.auth);

  const freePlanData = useMemo(() => {
    return featureData?.plans?.find((p) => p.code === "fi_free");
  }, [featureData]);

  const basicPlanData = useMemo(() => {
    return featureData?.plans?.find((p) => p.code === "fi_paid_basic");
  }, [featureData]);

  const showFreeCard = useMemo(() => {
    if (!freePlanData) return false;
    return !allowedPlanCodes || allowedPlanCodes.includes("fi_free");
  }, [freePlanData, allowedPlanCodes]);

  const showBasicCard = useMemo(() => {
    if (!basicPlanData) return false;
    return !allowedPlanCodes || allowedPlanCodes.includes("fi_paid_basic");
  }, [basicPlanData, allowedPlanCodes]);

  const [activePlan, setActivePlan] = useState("monthly");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (basicPlanData?.billing_options?.length > 0) {
      const hasMonthly = basicPlanData.billing_options.some(
        (opt) => opt.interval_key === "monthly",
      );
      if (!hasMonthly) {
        setActivePlan(basicPlanData.billing_options[0].interval_key);
      }
    }
  }, [basicPlanData]);

  const activeBillingOption = useMemo(() => {
    if (!basicPlanData?.billing_options) return null;
    return basicPlanData.billing_options.find(
      (opt) => opt.interval_key === activePlan,
    );
  }, [basicPlanData, activePlan]);

  const getBillingIntervalCopy = (intervalKey) =>
    content.pricing.billing_intervals[intervalKey];

  // ============================================================
  // ORCHESTRATED PLAN ACTION HANDLER
  // ============================================================
  const handlePlanSelection = async (planType) => {
    if (!isAuthenticated) {
      navigate("/sign-up");
      return;
    }

    setIsSubmitting(true);

    const isFree = planType === "fi_free";

    const selectedIntervalCode = isFree
      ? freePlanData?.billing_options?.[0]?.payment_interval_code || "MO"
      : activeBillingOption?.payment_interval_code || "MO";

    try {
      let subscriptionUuid = currentSubscriptionUuid;

      if (isUpgrade) {
        const response = await updateSubscriptionDetails(
          currentSubscriptionUuid,
          {
            action: "prepare_upgrade",
            plan_code: planType,
            interval: selectedIntervalCode,
          },
        );

        if (!(response.status === 200 || response.status === 201)) {
          throw new Error("Failed to prepare upgrade");
        }
      } else {
        const response = await createSubscription({
          plan: planType,
          payment_interval: selectedIntervalCode,
          is_checkout: true,
        });

        if (!(response.status === 200 || response.status === 201)) {
          throw new Error("Failed to create subscription draft");
        }

        const data = await response.json();
        subscriptionUuid = data.subscription?.uuid || data.uuid;
      }

      navigate("/checkout", {
        state: {
          breadcrumbs: [
            { label: "Financial Insights", to: "/finances/insights" },
            { label: "Check out" },
          ],
          service: "FI",
          billingType: "RECURRING",
          purchaseId: subscriptionUuid,

          // upgrade context
          isUpgradeDowngradePlan: isUpgrade,
          selectedPlanCode: planType,
          selectedIntervalCode,
        },
      });
    } catch (error) {
      console.error(
        "Network exception while configuring plan checkout:",
        error,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Fragment>
      <div
        className={`fi-subcription-plans ${isPopup ? "is-popup-mode" : ""}`}
        ref={sectionRef}
      >
        <div
          className="subcription-plans-header"
          style={
            isPopup
              ? {
                  flexDirection: "column",
                  gap: "15px",
                  alignItems: "flex-start",
                }
              : {}
          }
        >
          {!isPopup && (
            <div className="subcription-plans-header-left">
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <h3 className="h3-white">
                  {featureData?.name || content.pricing.title}
                </h3>
                <p className="p-white">
                  {featureData?.description || content.pricing.description}
                </p>
              </div>
            </div>
          )}

          {showBasicCard && (
            <div
              className="subcription-plans-header-right"
              style={isPopup ? { width: "100%" } : {}}
            >
              <div
                className="select-payment-container"
                style={isPopup ? { justifyContent: "flex-start" } : {}}
              >
                {basicPlanData?.billing_options?.map((option) => {
                  const intervalCopy = getBillingIntervalCopy(
                    option.interval_key,
                  );
                  return (
                    <div
                      key={option.interval_key}
                      className={`plan-item ${
                        activePlan === option.interval_key ? "active" : ""
                      }`}
                      onClick={() =>
                        !isSubmitting && setActivePlan(option.interval_key)
                      }
                    >
                      <p>{intervalCopy?.label || option.label}</p>
                      {(intervalCopy?.savings || option.discount_text) && (
                        <span>
                          {intervalCopy?.savings || option.discount_text}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div
          className="subcription-plans-body"
          style={{
            opacity: isSubmitting ? 0.6 : 1,
          }}
        >
          {showFreeCard && (
            <FiPlanCard
              price={
                freePlanData?.billing_options?.[0]?.price_per_cycle || "$0"
              }
              quote={freePlanData?.billing_options?.[0]?.quote || "Start Free"}
              name={content.plans.free.name}
              description={content.plans.free.description}
              ctaText={content.plans.free.cta}
              features={content.plans.free.features}
              onCtaClick={() => !isSubmitting && handlePlanSelection("fi_free")}
              isPopup={isPopup}
            />
          )}

          {showBasicCard && (
            <FiPlanCard
              price={activeBillingOption?.price_per_cycle || "$7"}
              quote={activeBillingOption?.quote || "Per Month"}
              name={content.plans.paid.name}
              description={content.plans.paid.description}
              ctaText={content.plans.paid.cta}
              isPopular={true}
              popularBadgeText={content.pricing.most_popular_badge}
              features={content.plans.paid.features}
              onCtaClick={() =>
                !isSubmitting && handlePlanSelection("fi_paid_basic")
              }
              isPopup={isPopup}
            />
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default FiSubscriptionPlans;
